/**
 * One error type for the whole frontend, whatever produced it:
 * - our API:   {type, message, errors: {field: [msg]}, request_id}
 * - allauth:   {status, errors: [{message, code, param}]}
 * - network / unexpected responses
 */

export type FieldErrors = Record<string, string[]>;

export interface ApiErrorInit {
  status: number;
  type: string;
  message: string;
  errors?: FieldErrors;
  requestId?: string | null;
  /** Raw body (e.g. allauth flow data on 401). */
  body?: unknown;
  cause?: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly type: string;
  readonly errors: FieldErrors;
  readonly requestId: string | null;
  readonly body: unknown;

  constructor(init: ApiErrorInit) {
    super(init.message, { cause: init.cause });
    this.name = "ApiError";
    this.status = init.status;
    this.type = init.type;
    this.errors = init.errors ?? {};
    this.requestId = init.requestId ?? null;
    this.body = init.body;
  }

  /** First message for a field, or for the form as a whole ("non_field_errors"). */
  fieldError(field: string): string | undefined {
    return this.errors[field]?.[0];
  }

  get formError(): string | undefined {
    return (
      this.errors.non_field_errors?.[0] ??
      (Object.keys(this.errors).length ? undefined : this.message)
    );
  }
}

interface OurErrorBody {
  type: string;
  message: string;
  errors?: Record<string, unknown>;
  request_id?: string | null;
}

interface AllauthErrorBody {
  status: number;
  errors?: { message: string; code: string; param?: string }[];
}

function isOurError(body: unknown): body is OurErrorBody {
  return typeof body === "object" && body !== null && "type" in body && "message" in body;
}

function isAllauthError(body: unknown): body is AllauthErrorBody {
  return (
    typeof body === "object" &&
    body !== null &&
    "status" in body &&
    ("errors" in body || "data" in body)
  );
}

function normaliseFieldErrors(errors: Record<string, unknown> | undefined): FieldErrors {
  const result: FieldErrors = {};
  for (const [field, value] of Object.entries(errors ?? {})) {
    result[field] = Array.isArray(value) ? value.map(String) : [String(value)];
  }
  return result;
}

const FALLBACK: Record<number, [string, string]> = {
  400: ["validation_error", "Please check the form and try again."],
  401: ["not_authenticated", "Please log in to continue."],
  403: ["permission_denied", "You don't have permission to do this."],
  404: ["not_found", "Not found."],
  409: ["conflict", "This changed in the meantime. Reload and try again."],
  429: ["throttled", "Too many requests. Please wait and try again."],
};

export function toApiError(status: number, body: unknown, requestId: string | null): ApiError {
  if (isOurError(body)) {
    return new ApiError({
      status,
      type: body.type,
      message: body.message,
      errors: normaliseFieldErrors(body.errors),
      requestId: body.request_id ?? requestId,
      body,
    });
  }
  if (isAllauthError(body)) {
    const errors: FieldErrors = {};
    for (const error of body.errors ?? []) {
      const field = error.param ?? "non_field_errors";
      (errors[field] ??= []).push(error.message);
    }
    const first = body.errors?.[0];
    const [fallbackType, fallbackMessage] = FALLBACK[status] ?? ["error", "Something went wrong."];
    return new ApiError({
      status,
      type: first?.code ?? fallbackType,
      message: first?.message ?? fallbackMessage,
      errors,
      requestId,
      body,
    });
  }
  const [type, message] = FALLBACK[status] ?? [
    "server_error",
    "Something went wrong on our side. Please try again.",
  ];
  return new ApiError({ status, type, message, requestId, body });
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}
